import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { adminApi, errorMessage } from "../../lib/api";
import { SelectField } from "../landing/Field";

const LIMIT = 50;
const ACTION_FILTERS = [
  { value: "login", label: "Logins" },
  { value: "logout", label: "Logouts" },
  { value: "kyc", label: "All KYC reviews" },
  { value: "kyc_approved", label: "KYC approved" },
  { value: "kyc_rejected", label: "KYC rejected" },
  { value: "export", label: "CSV exports" },
  { value: "member", label: "Team account changes" },
  { value: "aadhaar_viewed", label: "Aadhaar viewed" },
];
const COLOR = { kyc_approved: "text-emerald-300", kyc_rejected: "text-red-300", export: "text-sky-300", aadhaar_viewed: "text-amber-300" };
const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const ActivityDialog = ({ open, onClose }) => {
  const [members, setMembers] = useState([]);
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback((p) => {
    const params = { page: p, limit: LIMIT, ...(actor && { actor }), ...(action && { action }) };
    setLoading(true);
    adminApi.get("/admin/team/activity", { params })
      .then((r) => setData((prev) => ({ total: r.data.total, items: p === 1 ? r.data.items : [...prev.items, ...r.data.items] })))
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => setLoading(false));
  }, [actor, action]);

  useEffect(() => {
    if (!open) return;
    adminApi.get("/admin/team").then((r) => setMembers(r.data)).catch(() => {});
  }, [open]);
  useEffect(() => {
    if (!open) return;
    setPage(1);
    load(1);
  }, [open, load]);

  const more = () => { if (loading) return; setPage(page + 1); load(page + 1); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="activity-dialog" className="max-h-[90vh] max-w-5xl overflow-y-auto rounded-none border-white/10 bg-[#0F172A] text-slate-100">
        <DialogHeader><DialogTitle className="font-heading text-xl">Team activity log</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-3">
          <SelectField label="Team member" value={actor} onChange={setActor} placeholder="All members" testId="activity-filter-actor"
            options={members.map((m) => ({ value: m.email, label: `${m.name} (${m.email})` }))} />
          <SelectField label="Action" value={action} onChange={setAction} placeholder="All actions" testId="activity-filter-action" options={ACTION_FILTERS} />
          <button onClick={() => { setActor(""); setAction(""); }} data-testid="activity-filter-reset"
            className="h-11 border border-white/15 px-4 text-sm text-slate-300 hover:border-orange-500 hover:text-white">Clear filters</button>
        </div>
        <p data-testid="activity-count" className="text-sm text-slate-400">{data.total} entries</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-sm">
            <thead className="font-mono text-[11px] uppercase tracking-[0.12em] text-slate-400">
              <tr><th className="pb-2 pr-3">When</th><th className="pb-2 pr-3">Who</th><th className="pb-2 pr-3">Action</th><th className="pb-2 pr-3">On</th><th className="pb-2">Details</th></tr>
            </thead>
            <tbody>
              {data.items.map((a, i) => (
                <tr key={`${a.created_at}-${i}`} data-testid="activity-row" className="border-t border-white/5 align-top">
                  <td className="whitespace-nowrap py-2.5 pr-3 text-slate-400">{fmt(a.created_at)}</td>
                  <td className="py-2.5 pr-3"><p className="text-white">{a.actor_name}</p><p className="text-xs text-slate-500">{a.actor_email}</p></td>
                  <td className={`whitespace-nowrap py-2.5 pr-3 font-semibold ${COLOR[a.action] || "text-slate-200"}`}>{a.action_label}</td>
                  <td className="py-2.5 pr-3 text-slate-300">{a.target || "—"}</td>
                  <td className="py-2.5 text-slate-400">{a.details || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.items.length === 0 && <p data-testid="activity-empty" className="py-10 text-center text-slate-500">No activity yet.</p>}
        </div>
        {data.items.length < data.total && (
          <button onClick={more} disabled={loading} data-testid="activity-load-more" className="self-start border border-white/15 px-4 py-2 text-sm hover:border-white/40 disabled:opacity-50">{loading ? "Loading..." : "Load more"}</button>
        )}
      </DialogContent>
    </Dialog>
  );
};
